const pad = (n) => String(n).padStart(2, '0');
const daysIn = (year, month) => new Date(year, month + 1, 0).getDate();   // month is 0-based

// The cumulative of the last point dated on or before `cutoff` (YYYY-MM-DD),
// or 0 when there is none. The series has points only on days with spending.
export function cumulativeAt(points, cutoff) {
  let value = 0;
  for (const point of points || []) {
    if (point.date <= cutoff) value = point.cumulative || 0;
    else break;
  }
  return value;
}

/**
 * How this month's spending compares with last month's, for Home's pace
 * sentence. `trend` is the /dashboard/spending-trend response for one month;
 * `monthStart` is that month's first day and `today` the local date, both
 * YYYY-MM-DD.
 *
 * For the current month the two sides are compared at the same day: the
 * current total counts every movement of the month, including ones dated
 * later than today, so each side is read off its cumulative points instead,
 * last month's at the same day number (clamped to that month's length). For a
 * past month the whole months are compared. A future month has no pace, and
 * neither has a month whose previous month holds no spending: "more than
 * August" says nothing when August is empty, which is every user's first month.
 *
 * @returns {{ diff: number, atThisPoint: boolean } | null} diff > 0 means
 *   more spent than last month.
 */
export function spendingPace(trend, monthStart, today) {
  if (!trend || !monthStart || !today) return null;
  const year = Number(monthStart.slice(0, 4));
  const month = Number(monthStart.slice(5, 7)) - 1;
  const monthKey = monthStart.slice(0, 7);
  const todayKey = today.slice(0, 7);

  if (monthKey > todayKey) return null;
  const previousEmpty = !trend.previous?.points?.length;
  if (monthKey < todayKey) {
    if (previousEmpty && !trend.previous?.total) return null;
    const diff = trend.delta ?? ((trend.current?.total || 0) - (trend.previous?.total || 0));
    return { diff, atThisPoint: false };
  }

  if (previousEmpty) return null;
  const day = Number(today.slice(8, 10));
  const prevYear = month === 0 ? year - 1 : year;
  const prevMonth = month === 0 ? 11 : month - 1;
  const prevDay = Math.min(day, daysIn(prevYear, prevMonth));
  const prevCutoff = `${prevYear}-${pad(prevMonth + 1)}-${pad(prevDay)}`;

  const current = cumulativeAt(trend.current?.points, today);
  const previous = cumulativeAt(trend.previous?.points, prevCutoff);
  return { diff: current - previous, atThisPoint: true };
}
